// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  InventoryFilters,
  UNPAGINATED_INVENTORY_LIMIT,
  assemblyInventoryItemSchema,
  bulkItemInventoryItemSchema,
  groupedAssetInventoryItemSchema,
} from '@/inventory/api/types';
import { sparseLocationWithParentSchema } from '@/sparse/api/types';
import queryString from 'query-string';
import { z } from 'zod';

/**
 * Schema for inventory items grouped at a location
 */
const locationInventoryItemsSchema = z.object({
  bulkItems: z.array(bulkItemInventoryItemSchema),
  assets: z.array(groupedAssetInventoryItemSchema),
  assemblies: z.array(assemblyInventoryItemSchema),
});

/**
 * Schema for location with all its inventory (bulk items, assets, assemblies)
 */
const locationInventoryGroupSchema = z.object({
  location: sparseLocationWithParentSchema,
  items: locationInventoryItemsSchema,
});

/**
 * Schema for inventory by location data (wraps results array)
 * Maps to Go LocationInventoryResponse
 */
const inventoryByLocationSchema = z.object({
  results: z.array(locationInventoryGroupSchema),
});

/**
 * Response schema for inventory grouped by location
 * The backend LocationInventoryResponse gets wrapped in data/metadata by the JSON encoder
 */
const inventoryByLocationResponseSchema = z.object({
  data: inventoryByLocationSchema,
  metadata: responseMetadataSchema,
});

export type InventoryByLocationResponse = z.infer<
  typeof inventoryByLocationResponseSchema
>;

/**
 * Gets inventory grouped by location
 *
 * Endpoint: GET /v1/inventory/by-location/{type}
 *
 * @param type - Type of inventory: 'all', 'bulk-items', 'assets', or 'assemblies'
 * @param filters - Optional filters to apply to the inventory query
 * @returns Promise resolving to inventory grouped by location
 */
export async function getInventoryByLocation(
  type: 'all' | 'bulk-items' | 'assets' | 'assemblies' = 'all',
  filters?: InventoryFilters
) {
  const endpoint = `/v1/inventory/by-location/${type}`;

  const response = await api.get(endpoint, {
    paramsSerializer: (parameters) => queryString.stringify(parameters),
    params: { ...filters, limit: UNPAGINATED_INVENTORY_LIMIT },
  });
  return parseResponse(inventoryByLocationResponseSchema, response.data, {
    schemaName: 'InventoryByLocation',
    endpoint,
  });
}
